'use client';

import Link from 'next/link';
import {forwardRef} from 'react';

import type {MobileTopNavigationProps as Props} from './typings';

import Icon from '@/atoms/Icon/Icon';
import Logo from '@/atoms/Logo/Logo';
import NotificationWrapper from '@/atoms/Wrappers/Notification/NotificationWrapper';
import ProfileMenu from '@/molecules/Fragments/Menu/Navigation/Profile/ProfileMenu';
import Search from '@/molecules/Search/Search';
import {getSearchAutocomplete} from '@/redux/actions/searchActions';
import colors from '@/theme/constants/colors';
import constantsFactory from '@/utils/constants';
import {
  MenuIconButton,
  StyledListItem,
  StyledMobileTopNavigation,
  StyledTopNavigationList,
  StyledUserButtonsList,
  StyledUserButtonsListItem,
} from './styles';

const {DATA_TEST_ID} = constantsFactory();
const iconSize = 19;

const MobileTopNavigation = forwardRef<HTMLButtonElement, Props>(
  ({handleOpenSideMenu, auth, userSettings}, menuButtonRef): JSX.Element => {
    return (
      <StyledMobileTopNavigation data-testid={DATA_TEST_ID.MOBILE_NAVIGATION}>
        <StyledTopNavigationList as="div">
          <StyledListItem as="div" $has="menuIcon">
            <MenuIconButton
              onClick={handleOpenSideMenu}
              aria-label="Open side navigation menu"
              tabIndex={0}
              ref={menuButtonRef}
            >
              <Icon type="menu" fontSize={iconSize} label="Menu" />
            </MenuIconButton>
          </StyledListItem>

          <StyledListItem as="div" $has="search">
            <Search
              location="header"
              collapsible
              searchAction={getSearchAutocomplete}
              maxWidth="calc(100vw - 90px)"
            />
          </StyledListItem>

          <StyledListItem as="div" $has="logo">
            <Link href="/" aria-label="Go to Artfinder homepage" tabIndex={0}>
              <Logo width={120} height={22} color={colors.red100} />
            </Link>
          </StyledListItem>

          <StyledUserButtonsList>
            <StyledUserButtonsListItem>
              <ProfileMenu
                auth={auth}
                userSettings={userSettings}
                allowDropdown={false}
                tabIndex={0}
              />
            </StyledUserButtonsListItem>
            <StyledUserButtonsListItem $paddingTop={4}>
              <Link href="/basket/" aria-label="Your basket" tabIndex={0}>
                <NotificationWrapper count={userSettings.basket_count}>
                  <Icon type="trolley-empty" fontSize={iconSize} label="Checkout" />
                </NotificationWrapper>
              </Link>
            </StyledUserButtonsListItem>
          </StyledUserButtonsList>
        </StyledTopNavigationList>
      </StyledMobileTopNavigation>
    );
  },
);

MobileTopNavigation.displayName = 'MobileTopNavigation';

export default MobileTopNavigation;
